import React, { useState } from "react";
import { FileText, FileCheck2, Download, ExternalLink, Share2, ArrowLeft, Eye, EyeOff, Cpu, Globe, Users, Lock, Scale, X } from "lucide-react";
import { useBranding } from "@/context/BrandingContext";
import { useAuth } from "@/context/AuthContext";

const LEGAL_CODE = "founder";
const PATENT_BUNDLE = [
  {
    title: "Patent Addendum I — Technical Specification",
    desc: "Core method: staged occupancy logic, seat-to-sale reconciliation & the conditional Video Protocol · claims 1–13",
    pdf: "/BCS_Patent_Addendum_1_TechSpec_9d2f.pdf",
    docx: "/BCS_Patent_Addendum_1_TechSpec_9d2f.docx",
  },
  {
    title: "Patent Addendum II — Sensor-Agnostic Occupancy",
    desc: "Broadens sensing to any modality + multi-station session correlation · claims 14–31",
    pdf: "/BCS_Patent_Addendum_2_SensorAgnostic.pdf",
    docx: "/BCS_Patent_Addendum_2_SensorAgnostic.docx",
  },
  {
    title: "Patent Addendum III — Platform & Interface Embodiment",
    desc: "Edge-to-cloud data path, chair-state model, drill-down, RBAC & tenancy · claims 32–41",
    pdf: "/BCS_Patent_Addendum_3_Platform_c7e4.pdf",
    docx: "/BCS_Patent_Addendum_3_Platform_c7e4.docx",
  },
];

const VISION_DOCS = [
  {
    title: "Just Cuts — Australia · Master Prospectus & JV Proposal",
    desc: "50/50 North American joint-venture proposal for Just Cuts Australia (~190+ salons) · A$ · Sydney HQ front",
    pdf: "/Colosseum_JustCuts_Australia_Pitch_au7d3c.pdf",
    docx: "/Colosseum_JustCuts_Australia_Pitch_au7d3c.docx",
  },
  {
    title: "Just Cuts — Canada · Master Prospectus & JV Proposal",
    desc: "50/50 joint-venture proposal built around the Ontario expansion (Guelph · London · Toronto) · C$ · Canadian front",
    pdf: "/Colosseum_JustCuts_Canada_Pitch_ca9f21.pdf",
    docx: "/Colosseum_JustCuts_Canada_Pitch_ca9f21.docx",
  },
];

function LegalVault() {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [err, setErr] = useState(false);
  const [show, setShow] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    const entered = code.replace(/\s+/g, "").toLowerCase();
    if (entered === LEGAL_CODE.toLowerCase()) { setUnlocked(true); setErr(false); }
    else { setErr(true); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="legal-pill"
        className="inline-flex items-center gap-1.5 rounded-full border border-[#2A3550] bg-[#141B29] px-3 py-1 text-[11px] font-medium text-slate-400 hover:border-[#38BDF8]/60 hover:text-slate-200 transition-colors duration-200">
        <Lock className="h-3 w-3" /> Legal
      </button>
      {open && (
        <div className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" data-testid="legal-modal" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md rounded-2xl border border-[#2A3550] bg-[#171E2E] p-6 my-auto max-h-[85dvh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2.5">
                <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-[#1E2638] border border-[#2A3550]"><Scale className="h-5 w-5 text-[#38BDF8]" /></div>
                <div>
                  <h3 className="font-display text-lg font-medium text-white">Legal &amp; Patent</h3>
                  <p className="text-xs text-slate-400">Private — access code required</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="legal-close" className="inline-flex items-center gap-1.5 rounded-lg border border-[#2A3550] bg-[#1E2638] px-2.5 py-1.5 text-xs font-medium text-slate-300 hover:text-white hover:border-[#38BDF8] transition-colors duration-200"><ArrowLeft className="h-4 w-4" /> Back</button>
            </div>

            {!unlocked ? (
              <form onSubmit={submit} className="mt-5 space-y-3">
                <div className="relative">
                  <input type={show ? "text" : "password"} autoFocus value={code} onChange={(e) => { setCode(e.target.value); setErr(false); }}
                    autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                    data-testid="legal-code-input" placeholder="Enter access code"
                    className="w-full rounded-lg border border-[#2A3550] bg-[#0E131F] pl-4 pr-11 py-2.5 text-sm text-white placeholder-slate-500 focus:border-[#38BDF8] focus:outline-none" />
                  <button type="button" onClick={() => setShow((s) => !s)} data-testid="legal-code-toggle"
                    aria-label={show ? "Hide access code" : "Show access code"}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-[#38BDF8] transition-colors duration-200">
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {err && <p className="text-xs text-red-400" data-testid="legal-error">Incorrect access code.</p>}
                <button type="submit" data-testid="legal-unlock-btn"
                  className="w-full rounded-lg py-2.5 text-sm font-medium text-white hover:opacity-90 transition-opacity duration-200" style={{ backgroundColor: "var(--brand)" }}>
                  Unlock
                </button>
              </form>
            ) : (
              <div className="mt-5" data-testid="legal-unlocked">
                <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">Vision documents · confidential</div>
                <div className="space-y-3 mb-5">
                  {VISION_DOCS.map((d, i) => (
                    <div key={i} className="rounded-lg border border-[#232C40] bg-[#141B29] p-4" data-testid={`vision-doc-${i}`}>
                      <div className="text-sm font-medium text-white">{d.title}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{d.desc}</div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <a href={d.pdf} target="_blank" rel="noreferrer" data-testid={`vision-doc-${i}-pdf`}
                          className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-white hover:opacity-90 transition-opacity duration-200" style={{ backgroundColor: "var(--brand)" }}>
                          <ExternalLink className="h-4 w-4" /> Open PDF
                        </a>
                        <a href={d.pdf} download data-testid={`vision-doc-${i}-pdf-download`}
                          className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium border border-[#2A3550] bg-[#1E2638] text-slate-100 hover:border-[#38BDF8] transition-colors duration-200">
                          <Download className="h-4 w-4" /> PDF
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">Patent bundle · for counsel (pre-PCT)</div>
                <div className="space-y-3">
                  {PATENT_BUNDLE.map((doc, i) => (
                    <div key={i} className="rounded-lg border border-[#232C40] bg-[#141B29] p-4" data-testid={`legal-addendum-${i + 1}`}>
                      <div className="text-sm font-medium text-white">{doc.title}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{doc.desc}</div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <a href={doc.pdf} target="_blank" rel="noreferrer" data-testid={`legal-pdf-${i + 1}`}
                          className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-white hover:opacity-90 transition-opacity duration-200" style={{ backgroundColor: "var(--brand)" }}>
                          <ExternalLink className="h-4 w-4" /> Open PDF
                        </a>
                        <a href={doc.pdf} download data-testid={`legal-pdf-download-${i + 1}`}
                          className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium border border-[#2A3550] bg-[#1E2638] text-slate-100 hover:border-[#38BDF8] transition-colors duration-200">
                          <Download className="h-4 w-4" /> PDF
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-[11px] text-slate-500">Private legal drafts — read as one specification (Addenda I–III). Not part of the partner-facing document set. Not legal advice.</p>

                <div className="mt-5 rounded-lg border border-[#232C40] bg-[#141B29] p-4" data-testid="legal-target-switch">
                  <div className="text-sm font-medium text-white">Pitch target</div>
                  <div className="text-xs text-slate-400 mt-0.5 mb-3">Switch the platform's pitch documents, currency and map labelling between prospects.</div>
                  <TargetSwitch />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
import {
  PRIVATE_DOCS_ENABLED, TITAN_PDF, TITAN_DOCX, TITAN_TITLE,
  BRIEF_PDF, BRIEF_DOCX, BRIEF_TITLE, shareDoc, docSetFor,
} from "@/lib/prospectus";
import { TargetSwitch } from "@/components/TargetSwitch";

function DocCard({ testid, icon: Icon, color, brandBig, brandSmall, subtitle, docTitle, desc, pdf, docx, html, onOpen }) {
  return (
    <div className="surface-card p-6 flex flex-col min-w-0 w-full" data-testid={testid}>
      <div className="text-center min-w-0">
        <div className="font-display text-xl md:text-2xl font-medium tracking-tight leading-tight text-[#38BDF8]">{brandBig}</div>
        <h2 className="mt-0.5 font-display text-base md:text-lg font-medium leading-tight text-white">{brandSmall}</h2>
        {subtitle && <div className="mt-1 text-xs text-slate-400 break-words">{subtitle}</div>}
      </div>
      <p className="mt-4 text-sm text-slate-400 flex-1">{desc}</p>
      <div className="mt-5 flex flex-wrap gap-3">
        <button onClick={() => onOpen({ html, pdf, title: docTitle })} data-testid={`${testid}-open`}
          className="inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-white hover:opacity-90 transition-opacity duration-200" style={{ backgroundColor: "var(--brand)" }}>
          <Eye className="h-4 w-4" /> Open &amp; read
        </button>
        <a href={pdf} download data-testid={`${testid}-pdf`}
          className="inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium border border-[#2A3550] bg-[#1E2638] text-slate-100 hover:border-[#38BDF8] transition-colors duration-200">
          <Download className="h-4 w-4" /> Download PDF
        </a>
        <button onClick={() => shareDoc(docTitle, pdf)} data-testid={`${testid}-share`} aria-label="Share document"
          className="inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium border border-[#2A3550] bg-[#1E2638] text-slate-100 hover:border-[#38BDF8] transition-colors duration-200">
          <Share2 className="h-4 w-4" /> Share
        </button>
      </div>
    </div>
  );
}

function DocViewer({ doc, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-[#0A0E17]/95 backdrop-blur-sm" data-testid="doc-viewer">
      <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-[#2A3550] bg-[#0E131F]">
        <button onClick={onClose} data-testid="doc-viewer-back"
          className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs sm:text-sm font-medium text-white hover:opacity-90 transition-opacity duration-200 shrink-0" style={{ backgroundColor: "var(--brand)" }}>
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="min-w-0 flex-1 text-center sm:text-left">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold hidden sm:block">Vision Document</div>
          <div className="text-sm sm:text-base font-medium text-white truncate">{doc.title}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <a href={doc.pdf} download data-testid="doc-viewer-download"
            className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs sm:text-sm font-medium border border-[#2A3550] bg-[#1E2638] text-slate-100 hover:border-[#38BDF8] transition-colors duration-200">
            <Download className="h-4 w-4" /> <span className="hidden sm:inline">Download</span>
          </a>
          <a href={doc.pdf} target="_blank" rel="noreferrer" data-testid="doc-viewer-newtab"
            className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs sm:text-sm font-medium border border-[#2A3550] bg-[#1E2638] text-slate-100 hover:border-[#38BDF8] transition-colors duration-200">
            <ExternalLink className="h-4 w-4" /> <span className="hidden sm:inline">PDF</span>
          </a>
        </div>
      </div>
      <iframe src={doc.html || doc.pdf} title={doc.title} data-testid="doc-viewer-frame"
        className="flex-1 w-full bg-white" />
    </div>
  );
}

export default function Documents() {
  const { branding } = useBranding();
  const { user } = useAuth();
  const isHQ = user?.role === "corporate";
  const docs = docSetFor(branding.target || "sportclips");
  const [viewer, setViewer] = useState(null);

  return (
    <div data-testid="documents-page">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">Documents</div>
        {isHQ && <LegalVault />}
      </div>
      <h1 className="mt-2 font-display text-3xl md:text-4xl font-light tracking-tight">Vision Documents</h1>
      <p className="mt-2 text-sm text-slate-400 max-w-2xl">
        Open a document to read it right here on screen — tap Back any time to return to the platform. You can also download the PDF, or tap Share to send it to your team.
      </p>

      <div className="mt-8 grid gap-5 lg:grid-cols-2 max-w-4xl">
        {docs.pilot && (
          <DocCard
            testid="doc-card-pilot" icon={FileCheck2} color="#34D399"
            brandBig="Revenue Assurance" brandSmall="At a Glance"
            subtitle="BCS Seat-to-Sale · Pilot Program" docTitle={docs.pilot.title}
            desc="A one-page summary of the proposed 10–20 store pilot: scope, effort, how you verify it, success criteria, and our charitable mandate — ideal to forward internally after a first call."
            pdf={docs.pilot.pdf} docx={docs.pilot.docx} html={docs.pilot.html} onOpen={setViewer} />
        )}
        <DocCard
          testid="doc-card-pitch" icon={FileText} color={branding.primary_color}
          brandBig="Revenue Assurance" brandSmall={docs.prospectus.smallLabel || (docs.prospectus2 ? "Master Prospectus · Australia" : "Master Prospectus")}
          subtitle={docs.prospectus.title} docTitle={docs.prospectus.title}
          desc={docs.prospectus.desc || "The full prospectus: founder letter, patent status, projected impact, the pilot proposal, our charitable mandate, and the technical blueprint (hardware, staged threshold logic 6 / 15 / 20 min, security)."}
          pdf={docs.prospectus.pdf} docx={docs.prospectus.docx} html={docs.prospectus.html} onOpen={setViewer} />
        {docs.prospectus2 && (
          <DocCard
            testid="doc-card-pitch-2" icon={FileText} color={branding.primary_color}
            brandBig="Revenue Assurance" brandSmall="Master Prospectus · Canada"
            subtitle={docs.prospectus2.title} docTitle={docs.prospectus2.title}
            desc="The Canadian joint-venture proposal, built around the Ontario launch cohort (Guelph · London · Toronto): founder letter, patent status, the 50/50 JV framework in C$, our charitable mandate, and the technical blueprint."
            pdf={docs.prospectus2.pdf} docx={docs.prospectus2.docx} html={docs.prospectus2.html} onOpen={setViewer} />
        )}
      </div>

      <div className="mt-8 max-w-4xl">
        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-[#2A3550]" />
          <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Under the hood</span>
          <div className="h-px flex-1 bg-[#2A3550]" />
        </div>
        <div className="mt-5">
          <DocCard
            testid="doc-card-tech" icon={Cpu} color="#F59E0B"
            brandBig="Under the Hood" brandSmall="Platform &amp; Technical Breakdown"
            subtitle="For your IT & engineering team" docTitle={docs.tech.title}
            desc="A transparent, nerdy look at exactly what the platform is built from (React · FastAPI · MongoDB), the hardware layer, what already works vs. what's simulated, and a plain checklist of what your team connects to make it your own — even just for the pilot. The point: you adopt a working system, you don't build one."
            pdf={docs.tech.pdf} docx={docs.tech.docx} html={docs.tech.html} onOpen={setViewer} />
        </div>
      </div>

      {PRIVATE_DOCS_ENABLED && (
        <div className="mt-8 max-w-4xl">
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-[#2A3550]" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Confidential · Partner conversations</span>
            <div className="h-px flex-1 bg-[#2A3550]" />
          </div>
          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            <DocCard
              testid="doc-card-titan" icon={Globe} color="#38BDF8"
              brandBig="Global Licensing" brandSmall="Titan View"
              subtitle="Confidential · illustrative" docTitle={TITAN_TITLE}
              desc="The full-ceiling view of the global licensing opportunity if the pilot proves out: total addressable market, per-store economics, royalty scenarios up to the Titan case, the 50% charitable waterfall, and why publicly-traded operators feel this in reported earnings."
              pdf={TITAN_PDF} docx={TITAN_DOCX} onOpen={setViewer} />
            <DocCard
              testid="doc-card-brief" icon={Users} color="#F472B6"
              brandBig="Sector Targets" brandSmall="Points of Contact"
              subtitle="Confidential" docTitle={BRIEF_TITLE}
              desc="A shortlist of the strongest licensing targets — public operators, large franchise networks and adjacent verticals — with realistic first points of contact and the roles to aim for inside each company."
              pdf={BRIEF_PDF} docx={BRIEF_DOCX} onOpen={setViewer} />
          </div>
        </div>
      )}

      {viewer && <DocViewer doc={viewer} onClose={() => setViewer(null)} />}
    </div>
  );
}
